import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import { convertToIndianFormat } from "../../../utils/functions";
import { getWalletMetrics } from "../utils/walletMetrics";
import AreYouSureModal from "../../../components/modals/areYouSureModal";

const WITHDRAW_FEE_RATE = 0.0025;
const MIN_WITHDRAWAL = 5000;
const QUICK_AMOUNTS = [25000, 100000, 500000];

function maskAccountNumber(accountNumber) {
  const digits = String(accountNumber || "").replace(/\D/g, "");
  if (!digits) return "••••";
  return `•••• ${digits.slice(-4)}`;
}

function getWithdrawalBreakdown(amount) {
  const safeAmount = Number.isFinite(amount) && amount > 0 ? amount : 0;
  const processingFee = Math.round(safeAmount * WITHDRAW_FEE_RATE);
  const netReceivable = Math.max(0, safeAmount - processingFee);
  return { safeAmount, processingFee, netReceivable };
}

function SummaryLine({ label, value, emphasize = false, negative = false }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: emphasize ? 700 : 500 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{
          fontWeight: emphasize ? 800 : 700,
          color: negative ? "error.main" : "text.primary",
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {negative ? "-" : null}
        {value}
      </Typography>
    </Stack>
  );
}

function formatBankLabel(account) {
  const name = account?.bankName || "Bank";
  const masked = maskAccountNumber(account?.accountNumber);
  const primary = account?.isPrimary ? " (Primary)" : "";
  return `${name} ${masked}${primary}`;
}

function BankAccountSelect({ value, onChange, bankAccounts, helperText }) {
  const anchorRef = useRef(null);
  const [menuWidth, setMenuWidth] = useState();

  useLayoutEffect(() => {
    const el = anchorRef.current;
    if (!el) return undefined;

    const updateWidth = () => setMenuWidth(el.offsetWidth);
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Box ref={anchorRef} sx={{ width: "100%", minWidth: 0 }}>
      <TextField
        select
        fullWidth
        value={value}
        onChange={onChange}
        helperText={helperText}
        SelectProps={{
          renderValue: (selected) => {
            const account = bankAccounts.find((item) => item?._id === selected);
            return account ? formatBankLabel(account) : "";
          },
          MenuProps: {
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top", horizontal: "left" },
            PaperProps: menuWidth ? { style: { width: menuWidth } } : undefined,
          },
        }}
        sx={{
          "& .MuiInputBase-root": {
            minWidth: 0,
            width: "100%",
          },
        }}
      >
        {bankAccounts.map((account) => (
          <MenuItem key={account?._id} value={account?._id}>
            {formatBankLabel(account)}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}

export default function WithdrawTab({
  summary,
  bankAccounts = [],
  withdrawAmount,
  setWithdrawAmount,
  withdrawBankAccountId,
  setWithdrawBankAccountId,
  withdrawing,
  onWithdraw,
  setNetReceivable,
}) {
  const metrics = useMemo(() => getWalletMetrics(summary), [summary]);
  const availableBalance = metrics.available;
  const [areYouSureModalOpen, setAreYouSureModalOpen] = useState(false);

  const selectedBank = useMemo(
    () => bankAccounts.find((account) => account?._id === withdrawBankAccountId) || bankAccounts[0],
    [bankAccounts, withdrawBankAccountId]
  );

  const amount = Number(withdrawAmount);
  const { safeAmount, processingFee, netReceivable } = getWithdrawalBreakdown(amount);
  const isLocked = Boolean(summary?.locked);

  const amountError = useMemo(() => {
    if (!withdrawAmount) return "";
    if (!Number.isFinite(amount) || amount <= 0) return "Enter a valid amount";
    if (amount < MIN_WITHDRAWAL) return `Minimum withdrawal amount is ₹${MIN_WITHDRAWAL.toLocaleString("en-IN")}`;
    if (amount > availableBalance) return "Withdrawal amount cannot be greater than available balance";
    return "";
  }, [withdrawAmount, amount, availableBalance]);

  const bankError = useMemo(() => {
    if (!bankAccounts?.length) return "No approved bank accounts found";
    if (!withdrawBankAccountId) return "Select a bank account";
    return "";
  }, [bankAccounts, withdrawBankAccountId]);

  const canWithdraw =
    safeAmount >= MIN_WITHDRAWAL &&
    safeAmount <= availableBalance &&
    Boolean(withdrawBankAccountId) &&
    !isLocked &&
    !withdrawing;

  const handleMaxAmount = () => {
    setWithdrawAmount(String(Math.floor(availableBalance)));
    setNetReceivable(getWithdrawalBreakdown(availableBalance).netReceivable);
  };

  const handleQuickAmount = (value) => {
    setWithdrawAmount(String(Math.min(value, availableBalance)));
    setNetReceivable(getWithdrawalBreakdown(value).netReceivable);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={4}>
          <Stack spacing={2}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, letterSpacing: 0.6, color: "text.secondary" }}
                >
                  AVAILABLE FOR WITHDRAWAL
                </Typography>
                <Typography sx={{ mt: 1, fontWeight: 800, fontSize: "2rem", lineHeight: 1.1 }}>
                  {convertToIndianFormat(availableBalance)}
                </Typography>
                <Chip
                  icon={<CheckCircleRoundedIcon />}
                  label="Funds verified & ready"
                  size="small"
                  sx={{
                    mt: 1.5,
                    fontWeight: 700,
                    color: "success.dark",
                    bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
                  }}
                />
              </Box>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 3, borderColor: "divider" }}>
              <Box sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Box>
                    <Typography sx={{ fontWeight: 800 }}>Linked bank account</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      Withdrawals are credited here
                    </Typography>
                  </Box>
                  <Chip
                    icon={<CheckCircleRoundedIcon />}
                    label="Verified"
                    size="small"
                    sx={{
                      fontWeight: 700,
                      color: "success.dark",
                      bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
                    }}
                  />
                </Stack>

                {selectedBank ? (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      borderRadius: 2.5,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                          color: "primary.main",
                          flexShrink: 0,
                        }}
                      >
                        <AccountBalanceRoundedIcon fontSize="small" />
                      </Box>
                      <Stack spacing={0.35} minWidth={0}>
                        <Typography sx={{ fontWeight: 800 }}>{selectedBank?.bankName || "Bank"} {selectedBank?.isPrimary ? "(Primary)" : ""}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {maskAccountNumber(selectedBank?.accountNumber)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          IFSC: {selectedBank?.ifsc || "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Name: {selectedBank?.accHolderName || "-"}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    No approved bank account found.
                  </Typography>
                )}
              </Box>
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Stack spacing={2}>
            <Card variant="outlined" sx={{ borderRadius: 3, borderColor: "divider" }}>
              <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                <Typography sx={{ fontWeight: 800, fontSize: "1.15rem" }}>Initiate withdrawal</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Review fees and net receivable before confirming
                </Typography>

                <Stack spacing={2.25} sx={{ mt: 2.5 }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, letterSpacing: 0.5, color: "text.secondary", mb: 0.75, display: "block" }}
                    >
                      WITHDRAWAL AMOUNT
                    </Typography>
                    <TextField
                      fullWidth
                      value={withdrawAmount}
                      onChange={(e) => {
                        const next = e.target.value.replace(/[^\d]/g, "");
                        setWithdrawAmount(next);
                        setNetReceivable(getWithdrawalBreakdown(Number(next)).netReceivable);
                      }}
                      inputMode="numeric"
                      placeholder="0"
                      error={Boolean(amountError)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography sx={{ fontWeight: 700, color: "text.secondary" }}>₹</Typography>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              size="small"
                              onClick={handleMaxAmount}
                              disabled={!availableBalance}
                              sx={{ textTransform: "none", fontWeight: 700, minWidth: 0, px: 1 }}
                            >
                              Max
                            </Button>
                          </InputAdornment>
                        ),
                        sx: {
                          "& .MuiInputBase-root": {
                            minWidth: 0,
                            width: "100%",
                          },
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      color={amountError ? "error" : "text.secondary"}
                      sx={{ mt: 0.75, display: "block" }}
                    >
                      {amountError ? (
                        amountError
                      ) : (
                        <>
                          Minimum {convertToIndianFormat(MIN_WITHDRAWAL)} · Available {convertToIndianFormat(availableBalance)}
                        </>
                      )}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
                      {QUICK_AMOUNTS.map((value) => (
                        <Button
                          key={value}
                          size="small"
                          variant="outlined"
                          onClick={() => handleQuickAmount(value)}
                          disabled={!availableBalance}
                          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
                        >
                          {convertToIndianFormat(value)}
                        </Button>
                      ))}
                    </Stack>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, letterSpacing: 0.5, color: "text.secondary", mb: 0.75, display: "block" }}
                    >
                      BANK ACCOUNT
                    </Typography>
                    <BankAccountSelect
                      value={withdrawBankAccountId}
                      onChange={(e) => setWithdrawBankAccountId(e.target.value)}
                      bankAccounts={bankAccounts}
                      helperText={bankError}
                    />
                  </Box>
                </Stack>
              </Box>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 3, borderColor: "divider" }}>
              <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>Confirmation</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Amount breakdown
                    </Typography>
                  </Stack>
                </Stack>

                <Stack spacing={1.25} sx={{ mt: 2 }}>
                  <SummaryLine label="Withdrawal amount" value={convertToIndianFormat(safeAmount)} />
                  <SummaryLine
                    label="Processing fee (0.25%)"
                    value={convertToIndianFormat(processingFee)}
                    negative
                  />
                  <Divider sx={{ my: 0.5 }} />
                  <SummaryLine label="Net receivable" value={convertToIndianFormat(netReceivable)} emphasize />
                </Stack>

                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
                    border: "1px solid",
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.18),
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ScheduleRoundedIcon sx={{ fontSize: 18, color: "info.main" }} />
                    <Typography variant="body2" color="info.dark" sx={{ fontWeight: 600 }}>
                      Expected credit in 2 business days
                    </Typography>
                  </Stack>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  disabled={!canWithdraw}
                  onClick={() => setAreYouSureModalOpen(true)}
                  sx={{
                    mt: 2.5,
                    py: 1.35,
                    textTransform: "none",
                    fontWeight: 800,
                    borderRadius: 2,
                    fontSize: "0.98rem",
                  }}
                >
                  {isLocked
                    ? "Wallet locked"
                    : withdrawing
                      ? "Processing…"
                      : (
                          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            <Box component="span">Confirm withdrawal of</Box>
                            {convertToIndianFormat(netReceivable)}
                          </Box>
                        )}
                </Button>
              </Box>
            </Card>
          </Stack>
        </Grid>
      </Grid>
      <AreYouSureModal
        open={areYouSureModalOpen}
        setOpen={setAreYouSureModalOpen}
        handleYes={() => {
          if (!canWithdraw) return;
          onWithdraw();
          setAreYouSureModalOpen(false);
        }}
        message="Are you sure you want to withdraw this amount?"
        note={
          <>
            Withdrawal amount: {convertToIndianFormat(amount)}
          </>
        }
        publicAddress={selectedBank?.accountNumber}
        publicAddressMessage="Bank account number"
      />
    </Box>
  );
}
